import moment from 'moment';
import { DAY_OF_WEEK_MAP, DAY_OF_WEEK_TRANSLATION_MAP, FREQUENCY, I_CALENDAR_PROPERTY, MONTH_OF_YEAR_MAP, MONTH_OF_YEAR_TRANSLATION_MAP, R_RULE_PROPERTY, REVERSE_DAY_OF_WEEK_MAP, REVERSE_MONTH_OF_YEAR_MAP, WEEK_NUMBER_TRANSLATION_MAP, WEEK_OF_MONTH_TRANSLATION_MAP } from '../constants/iCalendar';
import I18n from 'I18n';

// Helper to get BYDAY rule part
const getByDayPart = byDay => {
  if (byDay && byDay.length > 0) {
    return [`${R_RULE_PROPERTY.BY_DAY}=${byDay.map(day => DAY_OF_WEEK_MAP[day]).join(',')}`];
  }
  return [];
};

// Helper to get BYMONTH rule part
const getByMonthPart = byMonth => {
  if (byMonth && byMonth.length > 0) {
    return [`${R_RULE_PROPERTY.BY_MONTH}=${byMonth.map(month => MONTH_OF_YEAR_MAP[month]).join(',')}`];
  }
  return [];
};

// Helper to get BYMONTHDAY rule part
const getByMonthDayPart = byMonthDay => {
  if (byMonthDay && byMonthDay.length > 0) {
    return [`${R_RULE_PROPERTY.BY_MONTH_DAY}=${byMonthDay.join(',')}`];
  }
  return [];
};

// Helper to get common rule parts (UNTIL, COUNT, INTERVAL, WEEKSTART)
const getCommonParts = config => {
  const parts = [];
  if (config.until) {
    const untilDateString = moment.unix(config.until).utc().format('YYYYMMDDTHHmmss[Z]');
    parts.push(`${R_RULE_PROPERTY.UNTIL}=${untilDateString}`);
  }
  if (config.count) {
    parts.push(`${R_RULE_PROPERTY.COUNT}=${config.count}`);
  }
  if (config.interval && config.interval > 1) {
    parts.push(`${R_RULE_PROPERTY.INTERVAL}=${config.interval}`);
  }
  if (config.weekStart) {
    parts.push(`${R_RULE_PROPERTY.WEEK_START}=${DAY_OF_WEEK_MAP[config.weekStart]}`);
  }
  return parts;
};

// Convert a recurrence rule config to an rrule string
export const recurrenceRuleConfigToRRule = config => {
  const freqPart = [`${R_RULE_PROPERTY.FREQ}=${config.frequency}`];
  const getFrequencyParts = () => {
    switch (config.frequency) {
      case FREQUENCY.DAILY:
        // BYDAY is optional for daily frequency per RFC-5545
        return getByDayPart(config.byDay);
      case FREQUENCY.WEEKLY:
        if (!config.byDay) {
          throw new Error('BYDAY is required for weekly frequency');
        }
        return getByDayPart(config.byDay);
      case FREQUENCY.MONTHLY:
        // Monthly supports BYMONTHDAY or BYDAY with embedded position per RFC-5545
        return [...getByMonthDayPart(config.byMonthDay), ...getByDayPart(config.byDay), ...getByMonthPart(config.byMonth)];
      case FREQUENCY.YEARLY:
        // BYMONTH is optional for yearly frequency per RFC-5545
        return [...getByMonthPart(config.byMonth), ...getByMonthDayPart(config.byMonthDay), ...getByDayPart(config.byDay)];
      default:
        throw new Error(`Invalid frequency: ${config.frequency}`);
    }
  };
  const parts = [...freqPart, ...getFrequencyParts(), ...getCommonParts(config)];
  return parts.join(';');
};

// Parse RRULE components into a key-value map
export const parseRruleComponents = rruleLine => {
  const rruleParts = rruleLine.replace(`${I_CALENDAR_PROPERTY.RRULE}:`, '').split(';');
  const rruleComponents = {};
  rruleParts.forEach(part => {
    const [key, value] = part.split('=');
    rruleComponents[key] = value;
  });
  return rruleComponents;
};

// Parse BYDAY values that can include position prefixes (e.g., "2FR", "-1MO")
const parseByDayValues = byDayString => {
  return byDayString.split(',').map(dayValue => {
    // Match pattern: optional position (1-4 or -1) followed by day abbreviation
    const match = dayValue.match(/^(-?\d+)?([A-Z]{2})$/);
    if (!match) {
      throw new Error(`Invalid BYDAY value: ${dayValue}`);
    }
    const [, positionStr, dayAbbrev] = match;
    const day = REVERSE_DAY_OF_WEEK_MAP[dayAbbrev];
    if (!day) {
      throw new Error(`Invalid day abbreviation: ${dayAbbrev}`);
    }
    return {
      day,
      position: positionStr ? positionStr : undefined
    };
  });
};

// Determine frequency type from RRULE components
const determineFrequency = rruleComponents => {
  switch (rruleComponents[R_RULE_PROPERTY.FREQ]) {
    case FREQUENCY.DAILY:
      return FREQUENCY.DAILY;
    case FREQUENCY.WEEKLY:
      return FREQUENCY.WEEKLY;
    case FREQUENCY.MONTHLY:
      return FREQUENCY.MONTHLY;
    case FREQUENCY.YEARLY:
      return FREQUENCY.YEARLY;
    default:
      throw new Error(`Unsupported frequency: ${rruleComponents[R_RULE_PROPERTY.FREQ]}`);
  }
};
export const iCalendarToRecurrenceConfig = ({
  iCalendarString,
  startDate
}) => {
  const rruleComponents = parseRruleComponents(iCalendarString);
  const frequency = determineFrequency(rruleComponents);

  // Parse BYDAY values which can include position prefixes
  const byDayWithPosition = rruleComponents[R_RULE_PROPERTY.BY_DAY] ? parseByDayValues(rruleComponents[R_RULE_PROPERTY.BY_DAY]) : undefined;

  // Extract just the days for backward compatibility
  const byDay = byDayWithPosition === null || byDayWithPosition === void 0 ? void 0 : byDayWithPosition.map(({
    day
  }) => day);
  return {
    frequency,
    until: rruleComponents[R_RULE_PROPERTY.UNTIL] ? moment(rruleComponents[R_RULE_PROPERTY.UNTIL]).unix() : undefined,
    count: rruleComponents[R_RULE_PROPERTY.COUNT] ? parseInt(rruleComponents[R_RULE_PROPERTY.COUNT], 10) : undefined,
    interval: rruleComponents[R_RULE_PROPERTY.INTERVAL] ? parseInt(rruleComponents[R_RULE_PROPERTY.INTERVAL], 10) : undefined,
    weekStart: rruleComponents[R_RULE_PROPERTY.WEEK_START] ? REVERSE_DAY_OF_WEEK_MAP[rruleComponents[R_RULE_PROPERTY.WEEK_START]] : undefined,
    byDay,
    byDayWithPosition,
    byMonth: rruleComponents[R_RULE_PROPERTY.BY_MONTH] ? rruleComponents[R_RULE_PROPERTY.BY_MONTH].split(',').map(month => REVERSE_MONTH_OF_YEAR_MAP[month]) : undefined,
    byMonthDay: rruleComponents[R_RULE_PROPERTY.BY_MONTH_DAY] ? rruleComponents[R_RULE_PROPERTY.BY_MONTH_DAY].split(',') : undefined,
    startDate
  };
};

// Helper function to get translated ordinal text
const getTranslatedOrdinal = value => {
  // Both WEEK_NUMBER_TRANSLATION_MAP and WEEK_OF_MONTH_TRANSLATION_MAP map to the same i18n keys
  const translationKey = WEEK_NUMBER_TRANSLATION_MAP[value] || WEEK_OF_MONTH_TRANSLATION_MAP[value];
  return translationKey ? I18n.text(translationKey) : String(value);
};

// Helper for interval-aware translation
const getIntervalAwareTranslation = (interval, baseKey, intervalKey, params = {}) => {
  return interval > 1 ? I18n.text(intervalKey, Object.assign({
    interval
  }, params)) : I18n.text(baseKey, params);
};

// Helper for monthly frequency patterns
const getMonthlyFrequencyLabel = config => {
  var _byDayWithPosition$;
  const {
    byMonthDay,
    byDayWithPosition,
    startDate,
    interval = 1
  } = config;
  if (byMonthDay && byMonthDay.length > 0) {
    // Pattern 1: By month day
    const monthDay = byMonthDay[0];
    const monthDayLabel = getTranslatedOrdinal(monthDay);
    return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.monthlyOnDay', 'meetings-icalendar-utils-lib.iCalendar.frequency.monthlyOnDayInterval', {
      ordinal: monthDayLabel
    });
  }
  if (byDayWithPosition && (_byDayWithPosition$ = byDayWithPosition[0]) !== null && _byDayWithPosition$ !== void 0 && _byDayWithPosition$.position) {
    // Pattern 2: By day with embedded position (e.g., "2FR")
    const {
      day,
      position
    } = byDayWithPosition[0];
    const dayNameFromPosition = I18n.text(DAY_OF_WEEK_TRANSLATION_MAP[day]);
    const weekOfMonthLabel = getTranslatedOrdinal(position);
    return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.monthlyOnNth', 'meetings-icalendar-utils-lib.iCalendar.frequency.monthlyOnNthInterval', {
      ordinal: weekOfMonthLabel,
      dayName: ` ${dayNameFromPosition}`
    });
  }
  if (startDate) {
    // Pattern 3: Use start date to determine day of month
    const dayOfMonth = moment.unix(startDate).date();
    const monthDayLabel = getTranslatedOrdinal(String(dayOfMonth));
    return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.monthlyOnDay', 'meetings-icalendar-utils-lib.iCalendar.frequency.monthlyOnDayInterval', {
      ordinal: monthDayLabel
    });
  }

  // Pattern 4: General monthly interval
  return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.monthly', 'meetings-icalendar-utils-lib.iCalendar.frequency.monthlyInterval');
};

// Helper for yearly frequency patterns
const getYearlyFrequencyLabel = config => {
  var _byDayWithPosition$2;
  const {
    byMonth,
    byMonthDay,
    byDayWithPosition,
    interval = 1
  } = config;

  // If no month specified, return basic yearly frequency
  if (!byMonth || byMonth.length === 0) {
    return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.yearly', 'meetings-icalendar-utils-lib.iCalendar.frequency.yearlyInterval');
  }
  const monthName = I18n.text(MONTH_OF_YEAR_TRANSLATION_MAP[byMonth[0]]);
  if (byMonthDay && byMonthDay.length > 0) {
    // Pattern 1: By month and month day
    const monthDayName = `${monthName} ${byMonthDay[0]}`;
    return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.yearlyOn', 'meetings-icalendar-utils-lib.iCalendar.frequency.yearlyOnInterval', {
      monthName: monthDayName
    });
  }
  if (byDayWithPosition && (_byDayWithPosition$2 = byDayWithPosition[0]) !== null && _byDayWithPosition$2 !== void 0 && _byDayWithPosition$2.position) {
    // Pattern 2: By month and day with embedded position
    const {
      day,
      position
    } = byDayWithPosition[0];
    const dayNameFromPosition = I18n.text(DAY_OF_WEEK_TRANSLATION_MAP[day]);
    const weekOfMonthLabel = getTranslatedOrdinal(position);
    const monthDayName = `${weekOfMonthLabel} ${dayNameFromPosition} of ${monthName}`;
    return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.yearlyOn', 'meetings-icalendar-utils-lib.iCalendar.frequency.yearlyOnInterval', {
      monthName: monthDayName
    });
  }

  // Default: Just by month
  return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.yearlyOn', 'meetings-icalendar-utils-lib.iCalendar.frequency.yearlyOnInterval', {
    monthName
  });
};
export const computeFrequencyLabelAndDayName = recurrenceConfig => {
  const {
    frequency,
    byDay,
    interval = 1,
    until,
    count
  } = recurrenceConfig;

  // RFC-5545: UNTIL and COUNT cannot both be present in the same rule
  if (until && count) {
    throw new Error('UNTIL and COUNT cannot both be present per RFC-5545');
  }

  // Helper to format multiple day names for weekly frequency
  const formatDayNames = days => {
    if (!days || days.length === 0) return '';
    if (days.length === 1) {
      return I18n.text(DAY_OF_WEEK_TRANSLATION_MAP[days[0]]);
    }

    // Format multiple days: "Monday, Thursday" or "Monday, Wednesday, Friday"
    const dayNames = days.map(day => I18n.text(DAY_OF_WEEK_TRANSLATION_MAP[day]));
    return dayNames.join(', ');
  };
  const dayName = byDay ? formatDayNames(byDay) : '';
  switch (frequency) {
    case FREQUENCY.DAILY:
      return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.daily', 'meetings-icalendar-utils-lib.iCalendar.frequency.dailyInterval');
    case FREQUENCY.WEEKLY:
      if (!byDay) {
        throw new Error('BYDAY is required for weekly frequency');
      }
      return getIntervalAwareTranslation(interval, 'meetings-icalendar-utils-lib.iCalendar.frequency.weeklyOn', 'meetings-icalendar-utils-lib.iCalendar.frequency.weeklyInterval', {
        dayName
      });
    case FREQUENCY.MONTHLY:
      return getMonthlyFrequencyLabel(recurrenceConfig);
    case FREQUENCY.YEARLY:
      return getYearlyFrequencyLabel(recurrenceConfig);
    default:
      throw new Error(`Invalid frequency: ${frequency}`);
  }
};